import type { ProtectedVaultRecord } from "@safesphere/contracts";

import { base64UrlToBytes, bytesToBase64Url } from "../offline/encoding.js";
import type { EncryptedPayload, EncryptedVaultRecord, VaultMetadata } from "./storageTypes.js";

export const vaultKdfIterations = 310_000;
const encoder = new TextEncoder();
const decoder = new TextDecoder();
const verifierValue = "SAFESPHERE_VAULT_VERIFIER_V1";

export type VaultUnlockResult =
  | { readonly ok: true; readonly key: CryptoKey }
  | { readonly ok: false; readonly reason: "INCORRECT_PASSPHRASE" | "VAULT_CORRUPTED" };

export async function deriveVaultKey(
  passphrase: string,
  salt: Uint8Array<ArrayBuffer>,
  iterations = vaultKdfIterations,
): Promise<CryptoKey> {
  if (passphrase.length < 8) throw new Error("PASSPHRASE_TOO_SHORT");
  const material = await crypto.subtle.importKey(
    "raw",
    encoder.encode(passphrase),
    "PBKDF2",
    false,
    ["deriveKey"],
  );
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", hash: "SHA-256", salt, iterations },
    material,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

export async function encryptBytes(
  value: Uint8Array<ArrayBuffer>,
  key: CryptoKey,
  context: string,
): Promise<EncryptedPayload> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv, additionalData: encoder.encode(context) },
    key,
    value,
  );
  return {
    algorithm: "AES-GCM",
    iv: bytesToBase64Url(iv),
    ciphertext: bytesToBase64Url(new Uint8Array(ciphertext)),
  };
}

export async function decryptBytes(
  payload: EncryptedPayload,
  key: CryptoKey,
  context: string,
): Promise<Uint8Array<ArrayBuffer>> {
  const value = await crypto.subtle.decrypt(
    {
      name: "AES-GCM",
      iv: base64UrlToBytes(payload.iv),
      additionalData: encoder.encode(context),
    },
    key,
    base64UrlToBytes(payload.ciphertext),
  );
  return new Uint8Array(value);
}

export async function createVaultMaterial(
  planId: string,
  passphrase: string,
  now = new Date().toISOString(),
): Promise<{ readonly metadata: VaultMetadata; readonly key: CryptoKey }> {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const key = await deriveVaultKey(passphrase, salt);
  const verifier = await encryptBytes(encoder.encode(verifierValue), key, `${planId}:verifier`);
  return {
    key,
    metadata: {
      id: planId,
      planId,
      schemaVersion: 1,
      salt: bytesToBase64Url(salt),
      iterations: vaultKdfIterations,
      verifier,
      createdAt: now,
    },
  };
}

export async function unlockVaultMaterial(
  metadata: VaultMetadata,
  passphrase: string,
): Promise<VaultUnlockResult> {
  let key: CryptoKey;
  try {
    key = await deriveVaultKey(passphrase, base64UrlToBytes(metadata.salt), metadata.iterations);
    const verifier = await decryptBytes(metadata.verifier, key, `${metadata.planId}:verifier`);
    if (decoder.decode(verifier) !== verifierValue) return { ok: false, reason: "VAULT_CORRUPTED" };
  } catch {
    return { ok: false, reason: "INCORRECT_PASSPHRASE" };
  }
  return { ok: true, key };
}

export async function encryptVaultRecord(
  record: ProtectedVaultRecord,
  key: CryptoKey,
): Promise<EncryptedVaultRecord> {
  return {
    id: record.id,
    planId: record.planId,
    schemaVersion: 1,
    payload: await encryptBytes(encoder.encode(JSON.stringify(record)), key, record.id),
    updatedAt: record.updatedAt,
  };
}

export async function decryptVaultRecord(
  record: EncryptedVaultRecord,
  key: CryptoKey,
): Promise<ProtectedVaultRecord> {
  return JSON.parse(
    decoder.decode(await decryptBytes(record.payload, key, record.id)),
  ) as ProtectedVaultRecord;
}
